// BRK-S145 — the document VOCABULARY and the upload RULES, both served by PMS.
//
// `GET /api/broker/document-types` (PMS-S332) publishes two things the portal
// must not restate:
//
//   • `types` — the types a broker may UPLOAD (the uploadable subset, not the
//     whole registry with a flag: PMS refuses to publish the rest, because
//     filtering it would be the SPA deciding its own permissions). Each row
//     carries its human label, so the picker and the Documents tab render
//     PMS's own wording rather than prettifying an enum value (BRK-S146).
//   • `rules` — the allowed extensions and content types, the per-file byte cap
//     and the per-field file count.
//
// NEVER HARDCODE EITHER. A client-side copy of the limits is the thing that
// silently disagrees with the server the day somebody widens one: a portal that
// rejects a `.webp` PMS now accepts strands the broker on a file that would have
// worked, and one that accepts an 11 MB file PMS refuses spends the upload before
// finding out. So the wizard screens against the SERVED rules and treats them as
// the API's own verdict rendered early (the `normalizeUsPhone` arrangement from
// BRK-S073) — not as a second, local policy.
//
// The screening here is a courtesy, not a gate: PMS enforces all of it again —
// plus the SNIFFED magic bytes, which a browser cannot read at all — and
// answers `400` with `details.code: DOCUMENT_REJECTED`, `details.filename` and
// `details.rule`, which is what the UI renders when a file gets past this.

import type { AuthedRequester } from "@/lib/brokerAuth";
import { sentenceCaseKey } from "@/lib/listFormat";

/**
 * What a document type attaches to. Mirrors PMS's closed enum VERBATIM
 * (`AccountDocumentTarget`): `account` — the insured's, full stop; `submission`
 * — it exists because of one order; `either` — the account owns it and an order
 * is the usual occasion.
 *
 * Read but not branched on. It is here because the vocabulary is published and
 * a closed mirror is how a widening becomes visible, not because this portal
 * has a decision that turns on it.
 */
export type DocumentTarget = "account" | "submission" | "either";

/**
 * PMS's document-type vocabulary, mirrored VERBATIM (PMS-S332's
 * `AccountDocumentType`). Closed on purpose: PMS's registry calls it a "CLOSED
 * VOCABULARY, CONSUMER-FIRST", so a widening lands there and then here.
 *
 * A DOCUMENT's own `type`, by contrast, stays a plain `string` wherever one is
 * read (`AccountDocument`, `BondDocument`) — a stored row whose type this
 * bundle has not heard of must still list and download, never disappear. This
 * union is the vocabulary of the PICKER, where a value we cannot name is a
 * value we must not offer.
 */
export type DocumentTypeKey =
  | "program_indication"
  | "gia"
  | "good_guy_letter"
  | "final_contract"
  | "supporting_document"
  | "financial_statement"
  | "work_in_progress_schedule"
  | "bank_letter"
  | "prior_bond_form"
  | "contract_or_specs"
  | "license"
  | "bond"
  // ── Signal's own statutory notices (PMS-S349) ─────────────────────────────
  //
  // Adopted at BRK-S160's re-pin, which is the first pin to carry it, and
  // adopted because the vocabulary is CLOSED: PMS publishes the whole enum on
  // `/api/broker/document-types`, so a member this bundle cannot name is a
  // parse this bundle cannot make. It is `brokerUploadable: false` AND
  // `brokerVisible: false` upstream, so nothing here renders one today — which
  // is the point of mirroring it rather than of rendering it.
  | "statutory_notice"
  | "other";

/**
 * What an upload with no `type` becomes, mirroring PMS's
 * `DEFAULT_BROKER_UPLOAD_TYPE`.
 *
 * Mirrored rather than left to the server because the PICKER needs a selected
 * value before the first upload, and a picker whose default disagreed with the
 * server's would show the broker one type and file another.
 */
export const DEFAULT_UPLOAD_TYPE: DocumentTypeKey = "supporting_document";

/** One entry of PMS's document-type vocabulary. */
export interface DocumentTypeInfo {
  key: DocumentTypeKey;
  label: string;
  /**
   * Whether a broker may upload this type DURING INTAKE.
   *
   * ⚠️ `GET /api/broker/document-types` serves ONLY the uploadable subset — PMS
   * refuses to publish the whole registry with a flag, because filtering it
   * would be the SPA deciding its own permissions. So this reads `true` on
   * every row that endpoint returns, and `uploadableTypes` is a belt-and-braces
   * filter rather than the thing that makes the picker correct.
   */
  brokerUploadable: boolean;
  /** Whether a broker may see one on their own account. An allowlist upstream;
   *  read here for completeness, never as a gate — a row a broker may not see
   *  never reaches them. */
  brokerVisible: boolean;
  target: DocumentTarget;
  /**
   * Whether a newer version REPLACES the older one (PMS-S335).
   *
   * `version` counts per (account, type), which means two different things:
   * "the second Program Indication" for a record Signal re-issues, and merely
   * "the second file the broker attached" for one they collect. So a client
   * marking a document out of date must branch on THIS rather than on
   * `superseded` alone — marking the first of two bank letters "Superseded"
   * says a true-looking thing that is false: nothing superseded it.
   *
   * PMS found this by driving the real flow, "which is the only place it shows
   * — every unit test until then had one document per type."
   */
  singular: boolean;
}

/** The upload limits PMS enforces, restated on screen before the request. */
export interface DocumentRules {
  /** Lowercase, dot-prefixed (`".pdf"`). Normalised on read — see `readRules`. */
  allowedExtensions: string[];
  allowedContentTypes: string[];
  maxBytesPerFile: number;
  maxFilesPerField: number;
}

export interface DocumentTypeCatalog {
  types: DocumentTypeInfo[];
  rules: DocumentRules;
}

/** GET /api/broker/document-types — the vocabulary + the rules, in one read. */
export async function getDocumentTypes(
  request: AuthedRequester,
): Promise<DocumentTypeCatalog> {
  const body = await request<DocumentTypeCatalog>("/api/broker/document-types");
  return { types: body.types, rules: readRules(body.rules) };
}

/**
 * The served rules, normalised for comparison.
 *
 * Extensions are lowercased and dot-prefixed whichever way PMS spells them, and
 * content types are lowercased, because a case-sensitive comparison against a
 * browser-supplied `File.type` is how a `.PDF` from a Windows share gets
 * refused for a rule it does not break. Normalising the SERVED side (rather
 * than only the file's) means the `accept` attribute and the rejection message
 * read the same as the check.
 */
function readRules(rules: DocumentRules): DocumentRules {
  return {
    ...rules,
    allowedExtensions: rules.allowedExtensions.map((ext) => {
      const lower = ext.trim().toLowerCase();
      return lower.startsWith(".") ? lower : `.${lower}`;
    }),
    allowedContentTypes: rules.allowedContentTypes.map((t) =>
      t.trim().toLowerCase(),
    ),
  };
}

/** A type's human label, or a humanised key when PMS's vocabulary is ahead of
 *  this bundle. Never the raw enum value: `bond_indemnity_agreement` on a
 *  broker's screen is our schema leaking, and it reads as a bug. */
export function documentTypeLabel(
  types: readonly DocumentTypeInfo[],
  key: string,
): string {
  const served = types.find((t) => t.key === key)?.label;
  return served ?? BROKER_VISIBLE_LABELS[key] ?? sentenceCaseKey(key);
}

/**
 * Labels for the broker-VISIBLE types the broker endpoint cannot name.
 *
 * ⚠️ A MIRROR, and a contract gap rather than a preference.
 * `GET /api/broker/document-types` serves the UPLOADABLE subset, but the
 * account document list publishes ten broker-VISIBLE types — so the four a
 * broker may see and never upload (`program_indication`, `final_contract`,
 * `bond`, and anything future) arrive on screen with no served label at all.
 * Humanising the key would print "Program Indication" for the first, which
 * happens to be right, and "Final Contract" for the second, which is WRONG:
 * PMS-S332 deliberately names it "Signed contract" because "final" reads as a
 * status where what matters is that it is signed.
 *
 * So these two strings are copied from PMS's registry VERBATIM, with the same
 * standing as any other mirror — and BRK-S147 is filed to have PMS publish
 * labels for broker-visible types, which is what deletes this table.
 */
const BROKER_VISIBLE_LABELS: Readonly<Record<string, string>> = {
  program_indication: "Program Indication",
  final_contract: "Signed contract",
  bond: "Bond",
};

/**
 * Whether a newer version of this type REPLACES the older — so `superseded` on
 * the older is a true statement worth marking.
 *
 * Defaults to TRUE for a type the served vocabulary does not name, and that is
 * not a coin toss: `/document-types` serves the **uploadable** subset, every
 * uploadable type is a COLLECTION (ten attachments are ten files, not ten
 * drafts of one), and every type it cannot name is one Signal ISSUES or
 * countersigns — where there is one current one by definition. So the types
 * missing from the vocabulary are exactly the singular ones, and the fallback
 * keeps the Program Indication letter's "Superseded" mark honest while
 * BRK-S147 waits on PMS publishing the broker-visible rows.
 */
export function isSingularDocumentType(
  types: readonly DocumentTypeInfo[],
  key: string,
): boolean {
  return types.find((t) => t.key === key)?.singular ?? true;
}

/**
 * A file's KIND from its content type — "PDF", "PNG image", "Text".
 *
 * The honest fallback for a stored reference whose document TYPE we do not hold
 * (a resumed order's value bag carries `contentType` and no `type`). It says
 * something true about the file rather than guessing at what Signal filed it
 * as, and it is deliberately derived from the content type rather than from the
 * filename — an extension is a claim, a content type is what was stored.
 */
export function fileKindLabel(contentType: string): string {
  const type = contentType.trim().toLowerCase();
  if (type === "application/pdf") return "PDF";
  if (type === "text/plain") return "Text";
  const image = /^image\/(.+)$/.exec(type);
  if (image) {
    const subtype = image[1] === "jpeg" ? "JPG" : image[1].toUpperCase();
    return `${subtype} image`;
  }
  return "Document";
}

/** The types a broker may upload — what a `type` picker offers. */
export function uploadableTypes(
  types: readonly DocumentTypeInfo[],
): DocumentTypeInfo[] {
  return types.filter((t) => t.brokerUploadable);
}

// ── Screening a chosen file (client-side, against the SERVED rules) ──────────

/**
 * Which rule a file breaks. Mirrors PMS's `details.rule` vocabulary VERBATIM
 * (PMS-S334) — six values, so both halves of the check speak one language and a
 * refusal the server made can be said in the words the client would have used.
 *
 * `type_not_uploadable` cannot be produced here: the picker offers only the
 * types the server served. It is mirrored anyway, because a closed reader that
 * silently dropped a published member would render a blank reason.
 */
export type DocumentRule =
  | "extension_not_allowed"
  | "content_type_mismatch"
  | "empty_file"
  | "too_large"
  | "too_many_files"
  | "type_not_uploadable";

/** The vocabulary as a value, for the runtime check a TS union cannot make. */
export const DOCUMENT_RULES: readonly DocumentRule[] = [
  "extension_not_allowed",
  "content_type_mismatch",
  "empty_file",
  "too_large",
  "too_many_files",
  "type_not_uploadable",
];

/** A file that will not be sent, and why — one sentence per rule, naming the
 *  file. A generic "invalid file" makes the broker guess which of four rules
 *  they broke. */
export interface DocumentRejection {
  filename: string;
  rule: DocumentRule;
  message: string;
}

/** A file's extension, lowercased and dot-prefixed, or "" when it has none. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0 || dot === filename.length - 1) return "";
  return filename.slice(dot).toLowerCase();
}

/**
 * A byte count as a broker reads it.
 *
 * BINARY units under decimal names (1 MB = 1024 KB), which is deliberately the
 * server's arithmetic: `maxBytesPerFile` is 10 MiB (10,485,760) and PMS's own
 * schema calls it "10 MB". Formatting decimally would print that cap as
 * "10.5 MB" and an 11 MiB file as "12 MB" — two figures nobody typed, in a
 * sentence whose whole job is to be checkable against the limit beside it.
 *
 * One decimal place below 10 MB so a 400 KB file does not read as "0 MB".
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${String(Math.round(kb))} KB`;
  const mb = kb / 1024;
  // A trailing `.0` is dropped: "over the 1.0 MB limit" reads like a precision
  // nobody asked for, where "1 MB" is the figure the limit is written as.
  return `${mb < 10 ? mb.toFixed(1).replace(/\.0$/, "") : String(Math.round(mb))} MB`;
}

/** The allowed extensions as a sentence: ".pdf, .png or .jpg". */
function extensionList(rules: DocumentRules): string {
  const list = rules.allowedExtensions;
  if (list.length === 0) return "a supported file type";
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(", ")} or ${list[list.length - 1]}`;
}

/**
 * What a broken rule reads as — ONE definition, used for the check made here
 * and for the one PMS makes.
 *
 * Both halves say the same sentence on purpose. A file the portal screens out
 * and a file PMS refuses broke the same rule, and a broker who sees two
 * different wordings for it learns that the two are different problems. It also
 * means the wording is fixed in one place rather than in a component's JSX.
 *
 * `size` and `count` need a figure, so they take one; when the caller has none
 * (a server refusal, where we know the rule but not the file's size) the
 * sentence states the LIMIT instead, which is the actionable half anyway.
 */
export function documentRuleMessage(
  rule: DocumentRule,
  filename: string,
  rules: DocumentRules,
  figures: { size?: number; cap?: number } = {},
): string {
  switch (rule) {
    case "extension_not_allowed":
      return `“${filename}” isn’t a file type we can accept — attach ${extensionList(rules)}.`;
    case "content_type_mismatch":
      // A DIFFERENT sentence from the one above, because it is a different
      // fact: the name says one thing and the bytes say another. PMS sniffs the
      // magic bytes, which a browser cannot, so this refusal only ever comes
      // from the server — and telling the broker their `.pdf` "isn't a type we
      // accept" would send them looking for a PDF they already have.
      return `“${filename}” doesn’t look like the file type its name says it is — check you attached the right file.`;
    case "empty_file":
      return `“${filename}” is empty — check the file saved properly and try again.`;
    case "too_large":
      return figures.size === undefined
        ? `“${filename}” is over the ${formatFileSize(rules.maxBytesPerFile)} limit.`
        : `“${filename}” is ${formatFileSize(figures.size)} — over the ${formatFileSize(rules.maxBytesPerFile)} limit.`;
    case "too_many_files": {
      const cap = figures.cap ?? rules.maxFilesPerField;
      return `You can attach up to ${String(cap)} ${cap === 1 ? "file" : "files"} here — remove one to add another.`;
    }
    case "type_not_uploadable":
      // Unreachable from the picker (it offers only served types), so this is
      // about a build a release behind rather than about the broker's file.
      return `We can’t file “${filename}” under that heading — reload the page and try again.`;
  }
}

/**
 * Whether a file may be sent, and why not.
 *
 * THREE OF PMS'S SIX RULES, deliberately — the three a client can actually
 * decide.
 *
 * `content_type_mismatch` is NOT checked here, and that is a correction rather
 * than an omission (PMS-S334): the server refuses a file whose SNIFFED magic
 * bytes disagree with its extension, and its own schema says `File.type` "is
 * never consulted — a browser derives it from the extension, which makes
 * believing it circular". A client-side content-type rule is therefore a rule
 * the server does not have: it can only refuse a file PMS would accept, which
 * is the exact failure this module exists to avoid. The browser cannot read a
 * byte of the file, so that rule stays the server's and renders when it fires.
 *
 * `type_not_uploadable` is unreachable — the picker offers only served types.
 *
 * `attachedCount` is how many files the FIELD already holds, so the count rule
 * is checked against the field's own cap — `maxFiles` on the configuration when
 * it names one, never more than PMS's `maxFilesPerField`. A field that asked for
 * more than the server allows is a configuration mistake, and the smaller of the
 * two is the honest limit to enforce and to say out loud.
 */
export function screenDocument(
  file: File,
  rules: DocumentRules,
  options: { attachedCount?: number; maxFiles?: number } = {},
): DocumentRejection | null {
  const filename = file.name;
  const cap = effectiveMaxFiles(rules, options.maxFiles);
  const reject = (rule: DocumentRule): DocumentRejection => ({
    filename,
    rule,
    message: documentRuleMessage(rule, filename, rules, {
      size: file.size,
      cap,
    }),
  });

  if ((options.attachedCount ?? 0) >= cap) return reject("too_many_files");
  if (!rules.allowedExtensions.includes(extensionOf(filename))) {
    return reject("extension_not_allowed");
  }
  // Before the size check: "over the limit" on a 0-byte file is nonsense, and
  // an empty file is its own rule upstream.
  if (file.size === 0) return reject("empty_file");
  if (file.size > rules.maxBytesPerFile) return reject("too_large");
  return null;
}

/** How many files a field may hold: its own `maxFiles` when it names one,
 *  capped by what PMS accepts per field. A configuration asking for more than
 *  the server allows is a mistake, and the limit that survives the upload is
 *  the honest one to enforce and to print. */
export function effectiveMaxFiles(
  rules: DocumentRules,
  maxFiles?: number,
): number {
  return Math.min(maxFiles ?? rules.maxFilesPerField, rules.maxFilesPerField);
}

/**
 * The `accept` attribute for a file input, from the served rules — so the system
 * picker offers what PMS takes rather than everything. Both spellings, because a
 * platform may match on either.
 *
 * Note this is the ONE place the served content types are still read. `accept`
 * is a HINT to the file picker, not a verdict: a drag-and-drop ignores it
 * entirely, and nothing is refused on its account. `screenDocument`
 * deliberately does not check a browser-supplied content type — see there.
 */
export function acceptAttribute(rules: DocumentRules): string {
  return [...rules.allowedContentTypes, ...rules.allowedExtensions].join(",");
}
